import { useEffect, useState } from "react";
import type { FC, ReactNode } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";
import { Box, Button, Collapse, ListItem, useTheme } from "@mui/material";
import type { ListItemProps } from "@mui/material";
import { useStyles } from "../theme/styles";

interface NavItemProps extends ListItemProps {
	active?: boolean;
	children?: ReactNode;
	depth: number;
	icon?: ReactNode;
	info?: ReactNode;
	open?: boolean;
	path?: string;
	title: string;
	sx?: any;
	onClick?: () => void;
	testId?: string;
}

const NavItem: FC<NavItemProps> = (propsWithoutDefaults) => {
	const props: NavItemProps = {
		...propsWithoutDefaults,
		active:
			propsWithoutDefaults.active !== undefined
				? propsWithoutDefaults.active
				: false,
		open:
			propsWithoutDefaults.open !== undefined
				? propsWithoutDefaults.open
				: false,
	};
	const classes = useStyles();
	const theme = useTheme();

	const {
		active,
		children,
		depth,
		icon,
		info,
		open: openProp,
		path,
		title,
		sx,
		onClick,
		testId,
		...other
	} = props;
	const [open, setOpen] = useState<boolean>(openProp);

	const handleToggle = (): void => {
		setOpen((prevOpen) => !prevOpen);

		if (!open) {
			onClick();
		}
	};

	useEffect(() => {
		if (!openProp) {
			setOpen(openProp);
		}
	}, [openProp]);

	let space = 16;

	if (depth > 0) {
		space = 32 + 3 * depth;
	}

	// Branch
	if (children) {
		return (
			<ListItem
				disableGutters
				sx={{
					display: "block",
					py: 0,
					ml: 1.5,
				}}
				{...other}
			>
				<Button
					data-testid={testId}
					onClick={handleToggle}
					startIcon={icon}
					sx={{
						color: theme.palette.text.primary,
						fontSize: depth === 0 ? "17px" : "15px",
						justifyContent: "flex-start",
						textAlign: "left",
						pl: `${space}px`,
						py: "12px",
						textTransform: "none",
						width: "220px",
						marginTop: "7px",
					}}
					variant="text"
				>
					<Box className={info ? undefined : classes.titleBox} sx={info ? { flexGrow: 0, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" } : undefined}>{title}</Box>
					{info && <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>{info}</Box>}
				</Button>
				<Collapse in={open && openProp}>{children}</Collapse>
			</ListItem>
		);
	}

	// Leaf
	return (
		<>
			<ListItem
				disableGutters
				sx={{
					display: "flex",
					py: 0,
					mt: -1,
					ml: 1.5,
					...sx,
				}}
			>
				<Button
					data-testid={testId}
					onClick={onClick}
					component={path && RouterLink}
					startIcon={icon}
					sx={{
						color: theme.palette.text.primary,
						fontSize: depth === 0 ? "17px" : "15px",
						justifyContent: "flex-start",
						textAlign: "left",
						pl: `${space}px`,
						mr: `${space}px`,
						py: "12px",
						pb: "6px",
						textTransform: "none",
						width: "220px",
						marginTop: "7px",
						fontWeight: openProp ? "700" : "600",
						...(active && {
							color: theme.palette.text.primary,
							fontWeight: "fontWeightBold",
							"& img": {
								filter: "invert(54%) sepia(27%) saturate(540%) hue-rotate(151deg) brightness(92%) contrast(93%)",
							},
						}),
					}}
					variant="text"
					to={path}
				>
					<Box className={info ? undefined : classes.titleBox} sx={info ? { flexGrow: 0, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" } : undefined}>{title}</Box>
					{info && <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>{info}</Box>}
				</Button>
			</ListItem>
		</>
	);
};

NavItem.propTypes = {
	active: PropTypes.bool,
	children: PropTypes.node,
	depth: PropTypes.number.isRequired,
	icon: PropTypes.node,
	info: PropTypes.node,
	open: PropTypes.bool,
	path: PropTypes.string,
	title: PropTypes.string.isRequired,
	sx: PropTypes.any,
};

export default NavItem;
